import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  Row,
  Modal,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Pagination,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faEye,
  faCalendarAlt,
  faUser,
  faFileAlt,
  faMoneyBillWave,
  faSearch,
  faFilter,
  faSync,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faTimesCircle,
  faPlay,
  faUserCheck,
  faGavel,
  faHandshake,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { fetchApi, getSector } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import CustomTable from "../../../components/CustomTable";
import CustomTypeahead from "../../components/CustomTypeahead";
import DaterangeSelector from "../../../components/DaterangeSelector";
import moment from "moment";
import { Routes } from "../../../routes";
import { useNavigate } from "react-router-dom";
import { useResponsive } from "../../../hooks/useResponsive";
import ResponsiveContainer from "../../../components/ResponsiveContainer";
import MobileDataCard from "../../../components/MobileDataCard";
import ResponsiveButton from "../../../components/ResponsiveButton";

// Enhanced status information for contractor schedules
const getStatusInfo = (status) => {
  const statusLower = status?.toLowerCase() || "";
  
  const statusMap = {
    'created': {
      title: 'Schedule Created',
      description: 'Payment schedule has been created and is ready for processing',
      icon: faPlay,
      type: 'info'
    },
    'pending': {
      title: 'Pending Processing',
      description: 'Awaiting processing by Sub Treasury office',
      icon: faClock,
      type: 'warning'
    },
    'processing': {
      title: 'Under Processing',
      description: 'Payment schedule is being processed',
      icon: faUserCheck,
      type: 'info'
    },
    'approved': {
      title: 'Approved',
      description: 'Payment schedule has been approved for payment',
      icon: faCheckCircle,
      type: 'success'
    },
    'paid': {
      title: 'Payment Completed',
      description: 'Payment has been successfully processed and completed',
      icon: faHandshake,
      type: 'success'
    },
    'returned': {
      title: 'Returned for Correction',
      description: 'Payment schedule has been returned for corrections',
      icon: faExclamationTriangle,
      type: 'warning'
    },
    'rejected': {
      title: 'Rejected',
      description: 'Payment schedule has been rejected',
      icon: faTimesCircle,
      type: 'danger'
    }
  };

  // Try to find exact match first
  if (statusMap[statusLower]) {
    return statusMap[statusLower];
  }

  // Try partial matches for complex statuses
  for (const [key, value] of Object.entries(statusMap)) {
    if (statusLower.includes(key)) {
      return value;
    }
  }

  // Default fallback
  return {
    title: status || 'Unknown Status',
    description: 'Status information is being processed',
    icon: faFileAlt,
    type: 'secondary'
  };
};

function SubTreasuryReports() {
  const { isMobile } = useResponsive();
  const history = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    paid: 0,
    totalAmount: 0
  });

  // Draft states — bound to inputs, never trigger API
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [mdaFilter, setMdaFilter] = useState("");
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

  // Committed states — only updated on "Apply Filters" or "Clear"
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedMda, setAppliedMda] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState(startDate);
  const [appliedEndDate, setAppliedEndDate] = useState(endDate);

  // Pagination
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, totalPages: 0
  });

  const [mdaOptions, setMdaOptions] = useState([]);
  useEffect(() => {
    getSector({ type: "mdas" }, "mdas", (data) => {
      setMdaOptions(
        (data || []).map((i) => ({ mda_name: i.description, mda_code: i.head }))
      );
    });
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);

    const paramObj = {
      page,
      limit: 50,
      userRole: user?.role || 'USER',
      userMdaCode: user?.mda_code || '',
      userId: user?.username || '',
      startDate: appliedStartDate,
      endDate: appliedEndDate,
    };
    if (appliedSearch.trim()) paramObj.search = appliedSearch.trim();
    if (appliedStatus) paramObj.status = appliedStatus;
    if (appliedMda.trim()) paramObj.mda_name = appliedMda.trim();

    const params = new URLSearchParams(paramObj);

    fetchApi(`get_sub_treasury_reports?${params.toString()}`)
      .then((data) => {
        if (data.success) {
          setList(data.result || []);
          setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
          setStatistics(data.statistics || {});
        }
      })
      .catch((err) => {
        console.error("Error fetching Sub Treasury reports:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, appliedSearch, appliedStatus, appliedMda, appliedStartDate, appliedEndDate, user?.role, user?.mda_code, user?.username]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedSearch(search);
    setAppliedStatus(statusFilter);
    setAppliedMda(mdaFilter);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaultStart = moment().subtract(30, 'days').format('YYYY-MM-DD');
    const defaultEnd = moment().format('YYYY-MM-DD');
    setSearch("");
    setStatusFilter("");
    setMdaFilter("");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setAppliedSearch("");
    setAppliedStatus("");
    setAppliedMda("");
    setAppliedStartDate(defaultStart);
    setAppliedEndDate(defaultEnd);
    setPage(1);
  };

  const handleViewHistory = (schedule) => {
    setSelectedSchedule(schedule);
    setShowHistoryModal(true);
  };

  const getStatusBadgeVariant = (status) => {
    const statusLower = status?.toLowerCase() || "";
    if (statusLower.includes("approved") || statusLower.includes("paid") || statusLower.includes("completed")) {
      return "success";
    } else if (statusLower.includes("pending") || statusLower.includes("processing")) {
      return "warning";
    } else if (statusLower.includes("returned") || statusLower.includes("rejected")) {
      return "danger";
    } else if (statusLower.includes("created")) {
      return "info";
    }
    return "primary";
  };

  // Role-based title and description
  const getRoleBasedTitle = () => {
    switch (user?.role) {
      case 'admin':
        return {
          title: "Sub Treasury Reports (Admin View)",
          description: "Track all Sub Treasury payment schedules across all MDAs"
        };
      case 'MDA ADMIN':
        return {
          title: "Sub Treasury Reports (Admin View)",
          description: "Track all Sub Treasury payment schedules across all MDAs"
        };
        // return {
        //   title: `Sub Treasury Reports (${user?.mda_name || 'MDA'} View)`,
        //   description: `Track Sub Treasury payment schedules for ${user?.mda_name || 'your MDA'}`
        // };
      case 'USER':
        return {
          title: "My Sub Treasury Activity",
          description: "Track payment schedules you have worked on in Sub Treasury operations"
        };
      default:
        return {
          title: "Sub Treasury Reports",
          description: "Track Sub Treasury payment schedules and activities"
        };
    }
  };

  const roleInfo = getRoleBasedTitle();

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === page}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={pagination.totalPages}
          onClick={() => setPage(pagination.totalPages)}
        >
          {pagination.totalPages}
        </Pagination.Item>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {((page - 1) * pagination.limit) + 1} to{" "}
          {Math.min(page * pagination.limit, pagination.total)} of{" "}
          {pagination.total} entries
        </div>
        <Pagination>
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          />
          {items}
          <Pagination.Next
            disabled={page === pagination.totalPages}
            onClick={() => setPage(p => p + 1)}
          />
        </Pagination>
      </div>
    );
  };

  const fields = [
    {
      title: "Batch ID",
      value: "batch_id",
    },
    {
      title: "PV Code",
      value: "pv_code",
    },
    {
      title: "MDA Name",
      value: "mda_name",
    },
    ...(user?.role === 'admin' || user?.role === 'MDA ADMIN' ? [{
      title: "MDA Code",
      value: "mda_code",
    }] : []),
    {
      title: "Contractor",
      value: "contractor_name",
    },
    {
      title: "Amount (N)",
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Status",
      custom: true,
      component: (item) => (
        <Badge color={getStatusBadgeVariant(item.payment_status)}>
          {item.payment_status || 'Created'}
        </Badge>
      ),
    },
    {
      title: "Date Created",
      custom: true,
      component: (item) => (
        <div>{moment(item.date).format("DD/MM/YYYY")}</div>
      ),
    },
    {
      title: "Actions",
      custom: true,
      component: (item) => (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => handleViewHistory(item)}
          >
            <FontAwesomeIcon icon={faHistory} className="me-1" />
            Details
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              history(
                `${Routes.paymentSchedule.viewStSchedule}/${item.batch_id}`
              );
            }}
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />
            View
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            {roleInfo.title}
          </h4>
          <p className="mb-0">
            {roleInfo.description} - Total: {pagination.total} Schedules
          </p>
          {/* {user?.role === 'MDA ADMIN' && (
            <small className="text-muted">
              <FontAwesomeIcon icon={faFilter} className="me-1" />
              Filtered by MDA Code: {user?.mda_code}
            </small>
          )} */}
          {user?.role === 'USER' && (
            <small className="text-muted">
              <FontAwesomeIcon icon={faUser} className="me-1" />
              Showing only schedules you have worked on
            </small>
          )}
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button variant="outline-primary" size="sm" onClick={fetchData}>
              <FontAwesomeIcon icon={faSync} className="me-1" />
              Refresh
            </Button>
            <Button variant="outline-primary" size="sm">
              Export
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-primary mb-2">
                <FontAwesomeIcon icon={faFileAlt} size="2x" />
              </div>
              <h5 className="mb-1">{statistics.total}</h5>
              <small className="text-muted">Total Schedules</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-warning mb-2">
                <FontAwesomeIcon icon={faClock} size="2x" />
              </div>
              <h5 className="mb-1">{statistics.pending}</h5>
              <small className="text-muted">Pending</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-info mb-2">
                <FontAwesomeIcon icon={faCheckCircle} size="2x" />
              </div>
              <h5 className="mb-1">{statistics.approved}</h5>
              <small className="text-muted">Approved</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-success mb-2">
                <FontAwesomeIcon icon={faMoneyBillWave} size="2x" />
              </div>
              <h5 className="mb-1">₦{formatNumber(statistics.totalAmount)}</h5>
              <small className="text-muted">Total Amount</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter Section */}
      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search Schedules</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Batch ID, PV Code, Contractor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  />
                  <Button variant="outline-primary" onClick={handleApplyFilters}>
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Created">Created</option>
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Approved">Approved</option>
                  <option value="Paid">Paid</option>
                  <option value="Returned">Returned</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>MDA Name</Form.Label>
                <CustomTypeahead
                  labelKey="mda_name"
                  options={mdaOptions}
                  selected={mdaFilter ? [{ mda_name: mdaFilter }] : []}
                  onChange={(s) => setMdaFilter(s.length ? s[0].mda_name : "")}
                  placeholder="Search MDA name..."
                  disabled={user?.role === 'MDA ADMIN'}
                />
                {user?.role === 'MDA ADMIN' ? (
                  <Form.Text className="text-muted">
                    Restricted to your MDA: {user?.mda_name}
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted">
                    Leave empty to search across all MDAs
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                {/* <Form.Label>Date Range</Form.Label> */}
                <DaterangeSelector
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={12} className="d-flex justify-content-center gap-2">
              <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                <FontAwesomeIcon icon={faFilter} className="me-1" />
                Apply
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-0" style={{ height: isMobile ? "auto" : "70vh", overflow: isMobile ? "visible" : "scroll" }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : list.length > 0 ? (
            isMobile ? (
              <div className="mobile-cards-container p-3">
                {list.map((item, index) => (
                  <MobileDataCard
                    key={index}
                    title={`${item.contractor_name}`}
                    data={item}
                    primaryField={{
                      label: "Amount",
                      value: item.amount,
                      type: "currency"
                    }}
                    secondaryFields={[
                      ...(user?.role === 'admin' || user?.role === 'MDA ADMIN' ? [{
                        label: "MDA Code",
                        value: item.mda_code
                      }] : []),
                      {
                        label: "Date Created",
                        value: item.created_at,
                        type: "date"
                      },
                      {
                        label: "Description",
                        value: item.description || "No description"
                      }
                    ]}
                    status={item.payment_status || 'Created'}
                    actions={[
                      {
                        type: "view",
                        label: "View Details",
                        icon: faEye,
                        variant: "primary"
                      }
                    ]}
                    onActionClick={(actionType, data) => {
                      if (actionType === "view") {
                        history(`${Routes.pv.pvReportView}?approval_no=${encodeURIComponent(data.approval_no)}&approval_type=${encodeURIComponent(data.approval_type || '')}`);
                      }
                    }}
                  />
                ))}
              </div>
            ) : (
              <CustomTable fields={fields} data={list} />
            )
          ) : (
            <div className="text-center p-5">
              <FontAwesomeIcon icon={faFileAlt} size="3x" className="text-muted mb-3" />
              <h5 className="text-muted">No Schedules Found</h5>
              <p className="text-muted">
                {appliedSearch || appliedStatus || appliedMda || appliedStartDate || appliedEndDate
                  ? "No payment schedules match your current filters. Try adjusting your search criteria."
                  : user?.role === 'USER'
                  ? "You haven't worked on any payment schedules yet, or no activity is recorded."
                  : user?.role === 'MDA ADMIN'
                  ? `No payment schedules found for ${user?.mda_name || 'your MDA'}.`
                  : "No payment schedules are available in the system."}
              </p>
            </div>
          )}
        </Card.Body>
        
        {/* Pagination */}
        {renderPagination()}
      </Card>

      {/* Schedule Details Modal */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center">
            <div className="me-3">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-primary" />
              Payment Schedule Details
            </div>
            <Badge variant="outline-primary" className="fs-6">
              {selectedSchedule?.batch_id}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSchedule && (
            <>
              {/* Schedule Summary */}
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Header className="bg-gradient-primary text-white">
                  <h6 className="mb-0 fw-bold">
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Payment Schedule Summary
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">Batch ID</div>
                        <div className="info-value text-primary fw-bold">{selectedSchedule.batch_id}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">PV Code</div>
                        <div className="info-value">{selectedSchedule.pv_code}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">MDA</div>
                        <div className="info-value">{selectedSchedule.mda_name}</div>
                        {selectedSchedule.mda_code && (
                          <div className="info-subtext">Code: {selectedSchedule.mda_code}</div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">Contractor</div>
                        <div className="info-value">{selectedSchedule.contractor_name}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">Amount</div>
                        <div className="info-value text-success fw-bold">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                          ₦{formatNumber(selectedSchedule.amount)}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label">Status</div>
                        <Badge color={getStatusBadgeVariant(selectedSchedule.payment_status)} className="status-badge">
                          {selectedSchedule.payment_status || 'Created'}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Additional Details */}
              <Row>
                <Col md={6}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <h6 className="mb-3">Payment Details</h6>
                      <div className="info-item mb-2">
                        <div className="info-label">Payment Type</div>
                        <div className="info-value">{selectedSchedule.payment_type}</div>
                      </div>
                      <div className="info-item mb-2">
                        <div className="info-label">Project Type</div>
                        <div className="info-value">{selectedSchedule.project_type}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Date Created</div>
                        <div className="info-value">{moment(selectedSchedule.date).format("DD MMM YYYY")}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <h6 className="mb-3">Banking Details</h6>
                      <div className="info-item mb-2">
                        <div className="info-label">Account Name</div>
                        <div className="info-value">{selectedSchedule.contractor_acc_name}</div>
                      </div>
                      <div className="info-item mb-2">
                        <div className="info-label">Account Number</div>
                        <div className="info-value">{selectedSchedule.contractor_acc_no}</div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Bank Name</div>
                        <div className="info-value">{selectedSchedule.contractor_bank_name}</div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="text-muted small">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
              Created: {selectedSchedule && moment(selectedSchedule.date).format("DD MMM YYYY, HH:mm")}
            </div>
            <div>
              <Button variant="outline-secondary" onClick={() => setShowHistoryModal(false)} className="me-2">
                <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                Close
              </Button>
              {selectedSchedule && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowHistoryModal(false);
                    history(
                      `${Routes.paymentSchedule.viewStSchedule}/${selectedSchedule.batch_id}`
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  View Full Schedule
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* Enhanced Summary Styles */
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff, #0056b3) !important;
        }
        
        .info-item {
          text-align: left;
        }
        
        .info-label {
          font-size: 0.875rem;
          color: #6c757d;
          font-weight: 500;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .info-value {
          font-size: 1rem;
          color: #212529;
          font-weight: 600;
          margin-bottom: 0.125rem;
        }
        
        .info-subtext {
          font-size: 0.75rem;
          color: #6c757d;
          font-style: italic;
        }
        
        .status-badge {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .info-item {
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </>
  );
}

export default SubTreasuryReports;